import { observer } from "mobx-react";
import React, { useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  IconButton,
  useTheme,
  Grid2,
} from "@mui/material";
import CustomTypography from "../../../components/common/form/CustomTypography";
import { Close as CloseIcon, Output as OutputIcon } from "@mui/icons-material";
import AppSearchDropDown2 from "../../../components/common/filter/AppSearchDropdown2";
import { FilterValue } from "../../../../models/Filters/FilterValue";
import { useStores } from "../../../../use-stores";
import { useNotification } from "../../../../utils/NotificationsContext";
import {
  PurchaseOrders,
  PurchaseOrderProduct,
  StockWithBoxes,
} from "../../../../models/SwageazyInventory/SwageazyInventory";
import CustomTextField from "../../../components/common/textFields/TextField";
import AppButton from "../../../components/common/buttons/AppButton";
import { VariantStock } from "../../../../models/SwageazyInventory/SwageazyInventory";

interface SwageazyInventoryOutwardPopupProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
}

const SwageazyInventoryOutwardPopup: React.FC<
  SwageazyInventoryOutwardPopupProps
> = ({ open, onClose, onSave }) => {
  const theme = useTheme();
  const { showError } = useNotification();
  const { addressStore, swageazyInventoryStore, warehouseStore } = useStores();

  useEffect(() => {
    if (!open) {
      swageazyInventoryStore.resetOutwardRequestFormData();
      if (swageazyInventoryStore.apiTimer) {
        clearTimeout(swageazyInventoryStore.apiTimer);
        swageazyInventoryStore.clearApiTimer();
      }
    } else {
      handleWarehouseSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    const blockedBoxes =
      swageazyInventoryStore.blockStockDetailsResponce?.boxes || [];
    const productId = swageazyInventoryStore.outwardRequestFormData?.product_id;
    const warehouseId =
      swageazyInventoryStore.outwardRequestFormData?.warehouse_id;

    if (blockedBoxes && blockedBoxes.length > 0 && productId && warehouseId) {
      const currentBoxes =
        swageazyInventoryStore.outwardRequestFormData?.boxes || [];

      const newBoxes = blockedBoxes.map((boxId: string) => {
        const existingBox = currentBoxes.find((b) => b?.boxes?.includes(boxId));

        if (existingBox) {
          return {
            ...existingBox,
            boxes: [boxId],
            stock: {
              ...existingBox?.stock,
              product_id: Number(productId),
              warehouse_id: String(warehouseId),
            },
          };
        }

        return {
          boxes: [boxId],
          sku: undefined,
          quantity: undefined,
          stock: {
            product_id: Number(productId),
            warehouse_id: String(warehouseId),
            quantity: 0,
          },
        };
      });

      if (
        currentBoxes.length === 0 ||
        currentBoxes.length !== newBoxes.length ||
        !currentBoxes.every(
          (box, index) => box?.boxes?.[0] === newBoxes[index]?.boxes?.[0],
        )
      ) {
        swageazyInventoryStore.setOutwardRequestFormData({
          boxes: newBoxes,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    swageazyInventoryStore.blockStockDetailsResponce?.boxes,
    swageazyInventoryStore.outwardRequestFormData?.product_id,
    swageazyInventoryStore.outwardRequestFormData?.warehouse_id,
  ]);

  // const handleCitySearch = (searchText?: string) => {
  //   swageazyInventoryStore.clearApiTimer();
  //   if (searchText && searchText.length > 2) {
  //     const timer = setTimeout(() => {
  //       addressStore
  //         .getCity({
  //           page_no: 1,
  //           search_text: searchText,
  //         })
  //         .catch(() => {});
  //     }, 1000);
  //     swageazyInventoryStore.setApiTimer(timer);
  //   }
  // };

  const handleWarehouseSearch = () => {
    if (warehouseStore.warehouses.length === 0) {
      warehouseStore.searchWarehouse().catch(() => {});
    }
  };

  const handlePoSearch = (searchText?: string) => {
    swageazyInventoryStore.clearApiTimer();
    if (searchText && searchText.length > 0) {
      const timer = setTimeout(() => {
        swageazyInventoryStore
          .getPurchaseOrderNumbers(searchText)
          .catch(() => {});
      }, 800);
      swageazyInventoryStore.setApiTimer(timer);
    } else {
      swageazyInventoryStore.setPoSearchResults([]);
      swageazyInventoryStore.setPoProducts([]);
      swageazyInventoryStore.setOutwardRequestFormData({
        po_id: undefined,
        product_id: undefined,
      });
    }
  };

  const getPurchaseOrderProducts = (poId?: number) => {
    const purchaseOrderId =
      poId ?? swageazyInventoryStore.outwardRequestFormData?.po_id ?? 0;
    if (purchaseOrderId <= 0) return;

    swageazyInventoryStore.swageazyInventoryAPI
      .getPurchaseOrderNumbers(String(purchaseOrderId))
      .then((response) => {
        if (
          response &&
          response.purchase_orders &&
          response.purchase_orders.length > 0
        ) {
          let products: PurchaseOrderProduct[] = [];
          let purchaseOrders: PurchaseOrders[] = [];

          purchaseOrders = response.purchase_orders;

          if (purchaseOrders.length > 0) {
            const selectedPO = purchaseOrders.find(
              (po: PurchaseOrders) =>
                po.po_id === purchaseOrderId ||
                String(po.po_id) === String(purchaseOrderId),
            );
            products = selectedPO?.products || [];
          }

          swageazyInventoryStore.setPoProducts(products);
        }
      })
      .catch(() => {});
  };

  const handlePoSelect = (selected: FilterValue[]) => {
    if (selected?.length > 0) {
      const poId = Number(selected[0]?.id);
      if (!isNaN(poId) && poId > 0) {
        swageazyInventoryStore.setOutwardRequestFormData({
          po_id: poId,
          product_id: undefined,
        });
        getPurchaseOrderProducts(poId);
      }
    } else {
      swageazyInventoryStore.setOutwardRequestFormData({
        po_id: undefined,
        product_id: undefined,
      });
      swageazyInventoryStore.setPoProducts([]);
    }
  };

  const handleProductSelect = (selected: FilterValue[]) => {
    if (selected?.length > 0) {
      const selectedId = Number(selected[0]?.id);
      const warehouseId =
        swageazyInventoryStore.outwardRequestFormData?.warehouse_id;
      const poId = swageazyInventoryStore.outwardRequestFormData?.po_id;

      if (selectedId && !isNaN(selectedId)) {
        const selectedProduct = swageazyInventoryStore.poProducts?.find(
          (p) => p?.id === selectedId,
        );

        if (selectedProduct) {
          const productId = selectedProduct?.product_id ?? selectedProduct?.id;

          if (productId) {
            swageazyInventoryStore.setOutwardRequestFormData({
              product_id: productId,
            });

            if (warehouseId && poId) {
              swageazyInventoryStore
                .BlockedQuantityDetails(poId, productId, warehouseId)
                .then(() => {})

                .catch((error: any) => {
                  showError(
                    "Error Occurred",
                    error?.response?.data?.message ||
                      "Failed to get blocked quantity details, please try again later",
                  );
                });
            }

            swageazyInventoryStore
              .getProductStockDetails(productId)
              .then(() => {})
              .catch((error: any) => {
                showError(
                  "Error Occurred",
                  error?.response?.data?.message ||
                    "Failed to get product stock details, please try again later",
                );
              });
          }
        }
      }
    } else {
      swageazyInventoryStore.setOutwardRequestFormData({
        product_id: undefined,
      });
    }
  };

  const getUniqueSKUs = () => {
    const skus = new Set<string>();
    const variantsStock =
      swageazyInventoryStore.productStockDetails?.variants_stock || [];
    variantsStock.forEach((variant: VariantStock) => {
      if (variant?.sku) {
        skus.add(variant.sku);
      }
    });
    return Array.from(skus).map((sku) => ({
      id: sku,
      value: sku,
    }));
  };

  const handleBoxSkuSelect = (boxId: string, selected: FilterValue[]) => {
    const currentBoxes =
      swageazyInventoryStore.outwardRequestFormData.boxes || [];
    const updatedBoxes = currentBoxes.map((box) =>
      box?.boxes?.includes(boxId)
        ? {
            ...box,
            sku: selected?.length > 0 ? selected[0]?.id : undefined,
            quantity: undefined,
            stock: {
              ...box?.stock,
              quantity: 0,
            },
          }
        : box,
    );
    if (!currentBoxes.find((b) => b?.boxes?.includes(boxId))) {
      updatedBoxes.push({
        boxes: [boxId],
        sku: selected.length > 0 ? selected[0].id : undefined,
        quantity: undefined,
        stock: {
          product_id: Number(
            swageazyInventoryStore.outwardRequestFormData.product_id,
          ),
          warehouse_id: String(
            swageazyInventoryStore.outwardRequestFormData.warehouse_id,
          ),
          quantity: 0,
        },
      });
    }
    swageazyInventoryStore.setOutwardRequestFormData({
      boxes: updatedBoxes,
    });
  };

  const handleBoxQuantityChange = (
    boxId: string,
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const quantity = Number(event.target.value);
    const currentBoxes =
      swageazyInventoryStore.outwardRequestFormData.boxes || [];
    const updatedBoxes = currentBoxes.map((box) =>
      box?.boxes?.includes(boxId)
        ? {
            ...box,
            quantity: isNaN(quantity) ? undefined : quantity,
            stock: {
              ...box?.stock,
              quantity: isNaN(quantity) ? 0 : quantity,
            },
          }
        : box,
    );
    if (!currentBoxes.find((b) => b?.boxes?.includes(boxId))) {
      updatedBoxes.push({
        boxes: [boxId],
        quantity: isNaN(quantity) ? undefined : quantity,
        stock: {
          product_id: Number(
            swageazyInventoryStore.outwardRequestFormData.product_id,
          ),
          warehouse_id: String(
            swageazyInventoryStore.outwardRequestFormData.warehouse_id,
          ),
          quantity: isNaN(quantity) ? 0 : quantity,
        },
      });
    }
    swageazyInventoryStore.setOutwardRequestFormData({
      boxes: updatedBoxes,
    });
  };

  const getBoxData = (boxId: string): StockWithBoxes => {
    const boxes = swageazyInventoryStore.outwardRequestFormData?.boxes || [];
    const found = boxes.find((b) => b?.boxes?.includes(boxId));
    if (found) {
      return found;
    }
    return {
      boxes: [boxId],
      stock: {
        product_id:
          Number(swageazyInventoryStore.outwardRequestFormData?.product_id) ||
          0,
        warehouse_id:
          String(swageazyInventoryStore.outwardRequestFormData?.warehouse_id) ||
          "",
        quantity: 0,
      },
    };
  };

  const canSave = (): boolean => {
    const formData = swageazyInventoryStore.outwardRequestFormData;
    const boxes = formData.boxes || [];

    if (
      !formData?.warehouse_id ||
      !formData?.po_id ||
      !formData?.product_id
    ) {
      return false;
    }

    const hasAtLeastOneQuantity = boxes.some(
      (box) =>
        box?.quantity !== undefined &&
        box?.quantity !== null &&
        box?.quantity > 0,
    );

    if (!hasAtLeastOneQuantity) {
      return false;
    }

    const boxesWithSku = boxes.filter(
      (box) => box?.sku !== undefined && box?.sku !== null && box?.sku !== "",
    );

    const allBoxesWithSkuHaveQuantity = boxesWithSku.every(
      (box) =>
        box?.quantity !== undefined &&
        box?.quantity !== null &&
        box?.quantity > 0,
    );

    return allBoxesWithSkuHaveQuantity;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        transition: {
          timeout: 300,
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        backdrop: {
          timeout: 300,
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        paper: {
          sx: {
            borderRadius: "16px",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
            overflow: "hidden",
            minHeight: { xs: "30vh", sm: "28vh" },
            maxHeight: { xs: "90vh", sm: "85vh", md: "75vh", lg: "70vh" },
            maxWidth: {
              xs: "80vw",
              sm: "80vw",
              md: "70vw",
              lg: "60vw",
              xl: "50vw",
            },
            width: {
              xs: "80vw",
              sm: "80vw",
              md: "70vw",
              lg: "60vw",
              xl: "50vw",
            },
            margin: { xs: "16px", sm: "32px" },
            background: `linear-gradient(135deg, ${theme.palette.customColors.white} 0%, ${theme.palette.customColors.lightGreyColor} 100%)`,
          },
        },
      }}
      sx={{
        zIndex: 2000,
        "& .MuiDialog-container": {
          alignItems: "center",
          justifyContent: "center",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          position: "relative",
          height: "100%",
          minHeight: { xs: "35vh", sm: "32vh" },
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, ${theme.palette.customColors.blueGradientStart} 0%, ${theme.palette.customColors.blueGradientEnd} 50%, ${theme.palette.customColors.blueGradientStart} 100%)`,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: { xs: "12px 16px 12px", sm: "16px 20px 16px" },
            borderBottom: `1px solid ${theme.palette.customColors.greyBorder}`,
          }}
        >
          <Box>
            {/* <CustomTypography
              variant="body2"
              style={{
                color: theme.palette.customColors.greyTextColor,
                marginBottom: 4,
              }}
            >
              Order: Swageazy Inventory
            </CustomTypography> */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <OutputIcon
                sx={{
                  color: theme.palette.customColors.blueGradientStart,
                  fontSize: 24,
                  marginTop: 0.2,
                }}
              />
              <CustomTypography variant="heading3">
                Outward Update
              </CustomTypography>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: theme.palette.customColors.greyTextColor,
              "&:hover": {
                backgroundColor: theme.palette.customColors.lightGreyColor,
                color: theme.palette.customColors.blackBorder,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent
          sx={{
            padding: { xs: "20px", sm: "24px" },
            flex: 1,
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          <Box
            sx={{
              mb: 1,
              p: 2,
              backgroundColor: theme.palette.customColors.lightGreyColor,
              borderRadius: "8px",
              border: `1px solid ${theme.palette.customColors.greyBorder}`,
            }}
          >
            <CustomTypography
              variant="body2"
              style={{
                color: theme.palette.text.secondary,
                lineHeight: "1.6",
                display: "flex",
                alignItems: "flex-start",
                gap: "8px",
              }}
            >
              <span
                style={{ fontWeight: 600, color: theme.palette.text.primary }}
              >
                Note:
              </span>
              <span>
                Select Warehouse → PO → Product. After selecting a product, box
                details will appear. Enter the SKU and quantity for the outward
                box.
              </span>
            </CustomTypography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 12, sm: 4, md: 4, lg: 4 }}>
                <CustomTypography
                  variant="heading6"
                  style={{ fontWeight: 600, marginBottom: 16 }}
                >
                  Warehouse{" "}
                  <span
                    style={{
                      color: theme.palette.error.main,
                      marginLeft: 4,
                    }}
                  >
                    *
                  </span>
                </CustomTypography>
                <AppSearchDropDown2
                  isMultiSelect={false}
                  title={"Select Warehouse"}
                  local={true}
                  backgroundColor={
                    theme.palette.customColors.grayBackGroundColor
                  }
                  borderColor={theme.palette.customColors.greyBodyBackground}
                  minWidth="13.938rem"
                  list={
                    warehouseStore.warehouses?.map((item) => {
                      return { id: String(item?.id), value: String(item?.id) };
                    }) || []
                  }
                  value={
                    warehouseStore.warehouses?.length > 0
                      ? (() => {
                          const warehouseId =
                            swageazyInventoryStore.outwardRequestFormData
                              ?.warehouse_id;
                          const warehouseName =
                            swageazyInventoryStore.outwardRequestFormData
                              ?.warehouse_name;

                          let warehouse = warehouseId
                            ? warehouseStore.warehouses?.find(
                                (w) => String(w?.id) === String(warehouseId),
                              )
                            : undefined;

                          if (!warehouse && warehouseName) {
                            warehouse = warehouseStore.warehouses?.find(
                              (w) =>
                                w?.city_sku === warehouseName ||
                                String(w?.id) === warehouseName,
                            );
                          }

                          return warehouse
                            ? [
                                {
                                  id: String(warehouse?.id),
                                  value: String(warehouse?.id),
                                },
                              ]
                            : [];
                        })()
                      : []
                  }
                  handleSelect={(selectedItem: FilterValue[]) => {
                    if (selectedItem && selectedItem.length > 0) {
                      const warehouseId = selectedItem[0].id;
                      const warehouseName = selectedItem[0].value;

                      const currentWarehouseId =
                        swageazyInventoryStore.outwardRequestFormData
                          ?.warehouse_id;
                      const poId =
                        swageazyInventoryStore.outwardRequestFormData?.po_id;
                      const productId =
                        swageazyInventoryStore.outwardRequestFormData
                          ?.product_id;

                      const isWarehouseChanged =
                        String(currentWarehouseId) !== String(warehouseId);

                      swageazyInventoryStore.setOutwardRequestFormData({
                        warehouse_id: warehouseId,
                        warehouse_name: warehouseName,
                      });

                      if (
                        isWarehouseChanged &&
                        poId &&
                        productId &&
                        warehouseId
                      ) {
                        swageazyInventoryStore.setOutwardRequestFormData({
                          boxes: [],
                        });

                        swageazyInventoryStore
                          .BlockedQuantityDetails(poId, productId, warehouseId)
                          .then(() => {})
                          .catch((error: any) => {
                            showError(
                              "Error Occurred",
                              error.response?.data?.message ||
                                "Failed to get blocked quantity details, please try again later",
                            );
                          });
                        swageazyInventoryStore
                          .getProductStockDetails(productId)
                          .then(() => {})
                          .catch((error: any) => {
                            showError(
                              "Error Occurred",
                              error.response?.data?.message ||
                                "Failed to get product stock details, please try again later",
                            );
                          });
                      } else if (
                        warehouseId &&
                        poId &&
                        productId &&
                        !isWarehouseChanged
                      ) {
                        swageazyInventoryStore
                          .BlockedQuantityDetails(poId, productId, warehouseId)
                          .then(() => {})
                          .catch((error: any) => {
                            showError(
                              "Error Occurred",
                              error.response?.data?.message ||
                                "Failed to get blocked quantity details, please try again later",
                            );
                          });
                        swageazyInventoryStore
                          .getProductStockDetails(productId)
                          .then(() => {})
                          .catch((error: any) => {
                            showError(
                              "Error Occurred",
                              error.response?.data?.message ||
                                "Failed to get product stock details, please try again later",
                            );
                          });
                      }
                    } else {
                      swageazyInventoryStore.setOutwardRequestFormData({
                        warehouse_id: undefined,
                        warehouse_name: undefined,
                      });
                      swageazyInventoryStore.setOutwardRequestFormData({
                        boxes: [],
                      });
                    }
                  }}
                />
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 4, md: 4, lg: 4 }}>
                <CustomTypography
                  variant="heading6"
                  style={{ fontWeight: 600, marginBottom: 16 }}
                >
                  PO Number{" "}
                  <span
                    style={{
                      color: theme.palette.error.main,
                      marginLeft: 4,
                    }}
                  >
                    *
                  </span>
                </CustomTypography>

                <AppSearchDropDown2
                  title="Search PO Number"
                  isMultiSelect={false}
                  list={swageazyInventoryStore.poSearchResults}
                  value={
                    swageazyInventoryStore.outwardRequestFormData?.po_id
                      ? [
                          {
                            id: String(
                              swageazyInventoryStore.outwardRequestFormData
                                ?.po_id,
                            ),
                            value: String(
                              swageazyInventoryStore.outwardRequestFormData
                                ?.po_id,
                            ),
                          },
                        ]
                      : []
                  }
                  handleSelect={handlePoSelect}
                  handleSearch={handlePoSearch}
                  local={false}
                  minWidth="100%"
                  borderRadius="8px"
                  disabled={
                    !swageazyInventoryStore.outwardRequestFormData?.warehouse_id
                  }
                  backgroundColor={
                    !swageazyInventoryStore.outwardRequestFormData?.warehouse_id
                      ? theme.palette.customColors.greyBodyBackground
                      : theme.palette.customColors.grayBackGroundColor
                  }
                />
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 4, md: 4, lg: 4 }}>
                <CustomTypography
                  variant="heading6"
                  style={{ fontWeight: 600, marginBottom: 16 }}
                >
                  PO Products{" "}
                  <span
                    style={{
                      color: theme.palette.error.main,
                      marginLeft: 4,
                    }}
                  >
                    *
                  </span>
                </CustomTypography>
                <AppSearchDropDown2
                  title="Select Product"
                  isMultiSelect={false}
                  list={(() => {
                    const seenProductIds = new Set<number>();
                    return (
                      swageazyInventoryStore.poProducts
                        ?.filter((product) => {
                          if (
                            product?.product_id &&
                            !seenProductIds.has(product.product_id)
                          ) {
                            seenProductIds.add(product.product_id);
                            return true;
                          }
                          return false;
                        })
                        .map((product) => ({
                          id: String(product?.id),
                          value: String(product?.name),
                        })) || []
                    );
                  })()}
                  value={
                    swageazyInventoryStore.outwardRequestFormData?.product_id
                      ? (() => {
                          const productId =
                            swageazyInventoryStore.outwardRequestFormData
                              ?.product_id;
                          const product =
                            swageazyInventoryStore.poProducts?.find(
                              (p) => p?.product_id === productId,
                            );
                          return product
                            ? [
                                {
                                  id: String(product?.id),
                                  value: product?.name || "",
                                },
                              ]
                            : [];
                        })()
                      : []
                  }
                  handleSelect={handleProductSelect}
                  local={true}
                  minWidth="100%"
                  borderRadius="8px"
                  disabled={
                    !swageazyInventoryStore.outwardRequestFormData
                      ?.warehouse_id ||
                    !swageazyInventoryStore.outwardRequestFormData?.po_id
                  }
                  backgroundColor={
                    !swageazyInventoryStore.outwardRequestFormData
                      ?.warehouse_id ||
                    !swageazyInventoryStore.outwardRequestFormData?.po_id
                      ? theme.palette.customColors.greyBodyBackground
                      : theme.palette.customColors.grayBackGroundColor
                  }
                />
              </Grid2>
            </Grid2>

            {/* Box Selection Section */}
            {swageazyInventoryStore.outwardRequestFormData?.product_id &&
              swageazyInventoryStore.blockStockDetailsResponce?.boxes?.length &&
              swageazyInventoryStore.blockStockDetailsResponce?.boxes?.length >
                0 && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    mt: 2,
                    p: 3,
                    backgroundColor: theme.palette.customColors.lightGreyColor,
                    borderRadius: "8px",
                    border: `1px solid ${theme.palette.customColors.greyBorder}`,
                  }}
                >
                  <CustomTypography
                    variant="heading6"
                    style={{ fontWeight: 600 }}
                  >
                    {swageazyInventoryStore.poProducts?.find(
                      (p) =>
                        p?.product_id ===
                        swageazyInventoryStore.outwardRequestFormData
                          ?.product_id,
                    )?.name || "Product"}
                    <span
                      style={{
                        fontWeight: 600,
                        marginLeft: 4,
                        color: theme.palette.customColors.greyTextColor,
                      }}
                    >
                      (Qty:{" "}
                      <span style={{ color: theme.palette.blue.main }}>
                        {
                          swageazyInventoryStore?.blockStockDetailsResponce
                            ?.stock?.quantity
                        }
                      </span>
                      )
                    </span>
                  </CustomTypography>
                  <CustomTypography
                    variant="body2"
                    style={{
                      color: theme.palette.customColors.greyTextColor,
                      marginBottom: 8,
                    }}
                  >
                    The quantity showing above is available to checkout. This is
                    blocked quantity against PRs.{" "}
                  </CustomTypography>

                  {swageazyInventoryStore.blockStockDetailsResponce?.boxes?.map(
                    (boxId: string, index: number) => {
                      const boxData = getBoxData(boxId);
                      return (
                        <Box
                          key={boxId || `box-${index}`}
                          sx={{
                            display: "flex",
                            gap: 2,
                            alignItems: "flex-start",
                            flexDirection: { xs: "column", sm: "row" },
                            flexWrap: { xs: "nowrap", sm: "wrap" },
                            p: 2,
                            backgroundColor: theme.palette.customColors.white,
                            borderRadius: "8px",
                            border: `1px solid ${theme.palette.customColors.greyBorder}`,
                          }}
                        >
                          {/* Box ID - Read Only */}
                          <Box sx={{ flex: 1, minWidth: "120px" }}>
                            <CustomTypography
                              variant="body2"
                              style={{
                                fontWeight: 600,
                                marginBottom: 8,
                                color: theme.palette.customColors.greyTextColor,
                              }}
                            >
                              Box ID
                            </CustomTypography>
                            <CustomTextField
                              width="100%"
                              allowNegativeValue={false}
                              placeholder="Box ID"
                              type="text"
                              value={boxId || ""}
                              disabled
                            />
                          </Box>

                          {/* SKU Selection */}
                          <Box sx={{ flex: 1, minWidth: "200px" }}>
                            <CustomTypography
                              variant="body2"
                              style={{
                                fontWeight: 600,
                                marginBottom: 8,
                                color: theme.palette.customColors.greyTextColor,
                              }}
                            >
                              Select SKU
                            </CustomTypography>
                            <AppSearchDropDown2
                              title="Select SKU"
                              isMultiSelect={false}
                              list={getUniqueSKUs()}
                              value={
                                boxData?.sku
                                  ? [{ id: boxData?.sku, value: boxData?.sku }]
                                  : []
                              }
                              handleSelect={(selected) =>
                                handleBoxSkuSelect(boxId, selected)
                              }
                              local={true}
                              minWidth="100%"
                              borderRadius="8px"
                            />
                          </Box>

                          {/* Quantity Input */}
                          <Box sx={{ flex: 1, minWidth: "150px" }}>
                            <CustomTypography
                              variant="body2"
                              style={{
                                fontWeight: 600,
                                marginBottom: 8,
                                color: theme.palette.customColors.greyTextColor,
                              }}
                            >
                              Enter qty
                            </CustomTypography>
                            <CustomTextField
                              width="100%"
                              allowNegativeValue={false}
                              placeholder="Enter quantity"
                              type="number"
                              value={boxData?.quantity || ""}
                              onChange={(e) =>
                                handleBoxQuantityChange(boxId, e)
                              }
                            />
                          </Box>
                        </Box>
                      );
                    },
                  )}
                </Box>
              )}
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            padding: { xs: "10px 16px 10px", sm: "16px 20px 20px" },
            borderTop: `1px solid ${theme.palette.customColors.greyBorder}`,
            backgroundColor: theme.palette.customColors.lightGreyColor,
            justifyContent: "flex-end",
            gap: 1,
          }}
        >
          <AppButton
            onClick={onClose}
            variant="grayscale"
            label="Cancel"
            customStyles={{
              borderColor: theme.palette.customColors.greyDarkborder,
              color: theme.palette.customColors.greyTextColor,
              fontWeight: 600,
              padding: "8px 20px",
              borderRadius: "8px",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                transform: "translateY(-1px)",
              },
            }}
          />

          <AppButton
            onClick={onSave}
            variant="primary"
            label="Save"
            disabled={!canSave()}
            customStyles={{
              backgroundColor: canSave()
                ? theme.palette.customColors.EmeraldGreen
                : theme.palette.customColors.greyDarkborder,
              color: theme.palette.customColors.white,
              fontWeight: 600,
              padding: "8px 20px",
              borderRadius: "8px",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                backgroundColor: canSave()
                  ? theme.palette.customColors.greenDark
                  : theme.palette.customColors.greyDarkborder,
                transform: canSave() ? "translateY(-1px)" : "none",
                boxShadow: canSave()
                  ? `0 4px 12px ${theme.palette.customColors.EmeraldGreen}30`
                  : "none",
              },
              "&:disabled": {
                backgroundColor: theme.palette.customColors.greyDarkborder,
                color: theme.palette.customColors.white,
                cursor: "not-allowed",
              },
            }}
          />
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default observer(SwageazyInventoryOutwardPopup);
